import { Theme, Alert, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAppSelector } from '../app/hooks';
import AssistPoolPanel from '../components/AssistRequest/AssistPoolPanel';
import IssueBacklogLobbyCard from '../components/IssueReport/IssueBacklogLobbyCard';
import TaskRouterBody from '../components/TaskRouterBody';
import useAssistPool from '../hooks/assistRequest/useAssistPool.hooks';
import useAssistAnswer from '../hooks/assistRequest/useAssistAnswer.hooks';
import { selectHasAssistPoolItems } from '../redux/features/assistRequest/assistRequest.selectors';
import { selectIsIssueReportingEnabled } from '../redux/features/config/config.selector';
import { selectOutstandingIssueReports } from '../redux/features/issueReport/issueReport.selectors';
import { AgentStatuses } from '../redux/features/taskRouter/taskRouter.constants';
import {
  selectAgentStatus,
  selectWSConnectionMessage,
} from '../redux/features/taskRouter/taskRouter.selector';
import logger from '../utils/logger';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  taskRouterPageClass: {
    /**
     * The lobby is ONE screen that never scrolls, and the backlog card is a real
     * child of it rather than an overlay — so the page is the flex column that
     * divides the space between the two.
     *
     * HITL-1575 first floated the card (`position: fixed`, bottom-left) to keep this
     * page's layout untouched. That was wrong: it covered the centred "Get ready"
     * message, which is the one thing this screen exists to say. `TaskRouterBody`
     * carries a definite height, so as a flex item that height is its BASIS and it
     * shrinks by exactly the card's height — the message re-centres in what is left
     * and nothing overlaps, with no change to the body itself.
     *
     * Properties added to the existing rule rather than a new class: this page has a
     * snapshot, and JSS numbers class names off a global rule counter, so a new rule
     * here renumbers unrelated suites.
     */
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  alertClass: {
    position: 'absolute',
    left: spacing(2),
    right: spacing(2),
    zIndex: 200,
    alignItems: 'center',
  },
}));

const TaskRouterPage = () => {
  const { taskRouterPageClass, alertClass } = useStyles();
  const {
    handleAgentAvailability,
    sendTaskMessage,
    initializeTaskRouter,
    isTaskAssignmentMode,
  } = useOutletContext<any>();
  const wsConnectionMessage = useAppSelector(selectWSConnectionMessage);
  // HITL-1792: the assist pool feed; inert until task-router assigns a request.
  useAssistPool();
  // HITL-1774: the card body answers through it (REST, `/v1/event`).
  const { sendAnswer } = useAssistAnswer();

  // What the lobby grid will show; the cards gate themselves the same way.
  const hasAssistPoolItems = useAppSelector(selectHasAssistPoolItems);
  const isIssueReportingEnabled = useAppSelector(selectIsIssueReportingEnabled);
  const outstandingReports = useAppSelector(selectOutstandingIssueReports);
  const agentStatus = useAppSelector(selectAgentStatus);
  const hasAssistItems = hasAssistPoolItems && !wsConnectionMessage;
  const hasIssueItems =
    isIssueReportingEnabled &&
    Boolean(isTaskAssignmentMode) &&
    outstandingReports.length > 0;
  const hasLobbyWork = hasAssistItems || hasIssueItems;
  // "Get ready" makes way for an assist card only. Only that screen:
  // Unavailable and Training keep their own content, with the grid below it.
  // The issue backlog alone leaves the lobby exactly as before 8.0.0.
  const hideGetReady =
    hasAssistItems &&
    Boolean(isTaskAssignmentMode) &&
    agentStatus === AgentStatuses.available;

  useEffect(() => {
    logger.info({
      message: 'Task-router page component is mounted',
      isTR: true,
    });
    initializeTaskRouter();
  }, []);

  return (
    <div data-testid="task-router-page" className={taskRouterPageClass}>
      {wsConnectionMessage ? (
        <Alert variant="filled" severity="error" className={alertClass}>
          {wsConnectionMessage}
        </Alert>
      ) : (
        // Hidden, never unmounted: `TaskRouterAgentAssignment` under it plays
        // the task-assigned sound. No wrapper: with no assist card the body is
        // rendered exactly as before.
        <TaskRouterBody
          handleAgentAvailability={handleAgentAvailability}
          sendTaskMessage={sendTaskMessage}
          isTaskAssignmentMode={isTaskAssignmentMode}
          hidden={hideGetReady}
        />
      )}

      {/* The lobby's work, as one grid of tiles: the assigned card
          (HITL-1773), then the issue backlog (HITL-1575). Two or three tiles a
          row by width; each tile keeps its own height. Rendered only when there
          is something, so a clean lobby is laid out exactly as before. With the
          backlog alone the wrapper is `display: contents`, so the backlog card
          is a direct flex item of the page, placed exactly as before 8.0.0; the
          wrapper stays mounted when a card arrives, so the backlog never
          remounts.

          The assist card is hidden while the task socket is down: assignments
          ride that socket, and the alert takes the lobby. The backlog stays
          reachable: its send is REST and independent of that socket.

          Both stay siblings of the body, never inside it — see the backlog
          card's note on `TaskRouterAcceptance`'s window-level keyup. With the
          body showing (Unavailable, Training, the WS alert) the grid sits
          below it at the bottom of the page. */}
      {hasLobbyWork && (
        <Box
          data-testid="task-router-lobby-grid"
          sx={
            hasAssistItems
              ? {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
                  alignItems: 'start',
                  alignContent: 'start',
                  gap: 2,
                  px: 4,
                  py: 3,
                  flex: hideGetReady ? 1 : '0 0 auto',
                  mt: hideGetReady ? 0 : 'auto',
                  minHeight: 0,
                  overflowY: 'auto',
                }
              : { display: 'contents' }
          }
        >
          {!wsConnectionMessage && <AssistPoolPanel onAnswer={sendAnswer} />}
          <IssueBacklogLobbyCard
            isTaskAssignmentMode={isTaskAssignmentMode}
            isLobbyTile={hasAssistItems}
          />
        </Box>
      )}
    </div>
  );
};

export default TaskRouterPage;
